package fr.frux.feature.treedetail.identity

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Apartment
import androidx.compose.material.icons.outlined.Download
import androidx.compose.material.icons.outlined.History
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.feature.treedetail.ImportedTreeSource
import fr.frux.feature.treedetail.TreeDetail
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_corrected
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_corrected_mine
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_inventory
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_moderation
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_never
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_osm
import fr.frux.feature.treedetail.resources.treedetail_identity_history
import fr.frux.feature.treedetail.resources.treedetail_identity_source_bordeaux
import fr.frux.feature.treedetail.resources.treedetail_identity_source_grenoble
import fr.frux.feature.treedetail.resources.treedetail_identity_source_lozere
import fr.frux.feature.treedetail.resources.treedetail_identity_source_lyon
import fr.frux.feature.treedetail.resources.treedetail_identity_source_metz
import fr.frux.feature.treedetail.resources.treedetail_identity_source_montpellier
import fr.frux.feature.treedetail.resources.treedetail_identity_source_nantes
import fr.frux.feature.treedetail.resources.treedetail_identity_source_osm
import fr.frux.feature.treedetail.resources.treedetail_identity_source_paris
import fr.frux.feature.treedetail.resources.treedetail_identity_source_plaine_commune
import fr.frux.feature.treedetail.resources.treedetail_identity_source_rennes
import fr.frux.feature.treedetail.resources.treedetail_identity_source_strasbourg
import fr.frux.feature.treedetail.resources.treedetail_identity_source_toulouse
import fr.frux.shared.presentation.relativeAgeLabel
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/** Pied du bloc Identité (52 dp, filet haut) : qui a corrigé en dernier, et l'accès à l'historique. */
@OptIn(ExperimentalTime::class)
@Composable
internal fun IdentityFooterRow(
    tree: TreeDetail,
    correctedByMe: Boolean,
    onHistory: () -> Unit,
) {
    val scheme = MaterialTheme.colorScheme
    val footer = identityFooter(tree, correctedByMe)
    val (icon, text) = footer.iconAndText()
    Column {
        HorizontalDivider(color = scheme.outlineVariant)
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .heightIn(min = 52.dp)
                    .padding(start = 16.dp, end = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = scheme.onSurfaceVariant,
                modifier = Modifier.size(18.dp),
            )
            Text(
                text = text,
                style = MaterialTheme.typography.bodyMedium,
                color = scheme.onSurfaceVariant,
                modifier = Modifier.weight(1f),
            )
            FruxButton(
                text = stringResource(Res.string.treedetail_identity_history),
                onClick = onHistory,
                style = FruxButtonStyle.Text,
            )
        }
    }
}

@OptIn(ExperimentalTime::class)
@Composable
private fun IdentityFooter.iconAndText(): Pair<ImageVector, AnnotatedString> =
    when (this) {
        is IdentityFooter.Corrected -> {
            val age = relativeAgeLabel(atMillis, Clock.System.now().toEpochMilliseconds())
            if (mine) {
                Icons.Outlined.History to
                    AnnotatedString(stringResource(Res.string.treedetail_identity_footer_corrected_mine, age))
            } else {
                val author = authorName ?: stringResource(Res.string.treedetail_identity_footer_moderation)
                Icons.Outlined.History to
                    stringResource(Res.string.treedetail_identity_footer_corrected, age, author).withBold(author)
            }
        }

        is IdentityFooter.Imported -> {
            val name = stringResource(source.identityName())
            val template =
                if (source == ImportedTreeSource.OSM) {
                    Res.string.treedetail_identity_footer_osm
                } else {
                    Res.string.treedetail_identity_footer_inventory
                }
            val icon = if (source == ImportedTreeSource.OSM) Icons.Outlined.Download else Icons.Outlined.Apartment
            icon to stringResource(template, name).withBold(name)
        }

        IdentityFooter.NeverCorrected -> {
            Icons.Outlined.History to AnnotatedString(stringResource(Res.string.treedetail_identity_footer_never))
        }
    }

/** Met en gras (couleur pleine) la dernière occurrence de [part], le nom propre de la phrase. */
@Composable
private fun String.withBold(part: String): AnnotatedString {
    val color = MaterialTheme.colorScheme.onSurface
    val start = lastIndexOf(part)
    return buildAnnotatedString {
        append(this@withBold)
        if (start >= 0) addStyle(SpanStyle(fontWeight = FontWeight.SemiBold, color = color), start, start + part.length)
    }
}

private fun ImportedTreeSource.identityName(): StringResource =
    when (this) {
        ImportedTreeSource.OSM -> Res.string.treedetail_identity_source_osm
        ImportedTreeSource.RENNES -> Res.string.treedetail_identity_source_rennes
        ImportedTreeSource.MONTPELLIER -> Res.string.treedetail_identity_source_montpellier
        ImportedTreeSource.BORDEAUX -> Res.string.treedetail_identity_source_bordeaux
        ImportedTreeSource.LYON -> Res.string.treedetail_identity_source_lyon
        ImportedTreeSource.STRASBOURG -> Res.string.treedetail_identity_source_strasbourg
        ImportedTreeSource.NANTES -> Res.string.treedetail_identity_source_nantes
        ImportedTreeSource.TOULOUSE -> Res.string.treedetail_identity_source_toulouse
        ImportedTreeSource.PLAINE_COMMUNE -> Res.string.treedetail_identity_source_plaine_commune
        ImportedTreeSource.PARIS -> Res.string.treedetail_identity_source_paris
        ImportedTreeSource.GRENOBLE -> Res.string.treedetail_identity_source_grenoble
        ImportedTreeSource.METZ -> Res.string.treedetail_identity_source_metz
        ImportedTreeSource.LOZERE -> Res.string.treedetail_identity_source_lozere
    }
